import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { PortalShell } from "@pulse-business-ai/portal-shell";
import { RouterLink } from "@/components/layout/RouterLink";
import {
  HUB_COMMAND_PALETTE_QUICK_ACTIONS,
  getFilteredNavSections,
} from "@/components/layout/config";
import { NotificationToast } from "./NotificationToast";
import { ThemeProvider } from "./ThemeProvider";
import { Breadcrumb } from "./Breadcrumb";
import { RouteProgressBar } from "./RouteProgressBar";
import { PinnedNav } from "./PinnedNav";
import {
  KeyboardShortcutsModal,
  useKeyboardShortcuts,
} from "./KeyboardShortcutsModal";
import { useAuth } from "@/core/auth";
import { OnboardingTour } from "./OnboardingTour";

interface HubAppLayoutProps {
  children: React.ReactNode;
}

/**
 * Map the authenticated user's Supabase role list to the PortalShell's
 * `PulseUserRole` shape. PortalShell uses this to gate `tier: 'internal'`
 * HubSwitcher destinations (operations admin) — only admin/staff/sys see them.
 *
 * pulse-hub uses `viewer | member | admin` for its Supabase roles. Mapping:
 *   - admin role present  → "admin"  (sees internal hubs)
 *   - otherwise           → "member" (treated as customer-tier)
 *
 * Backend ACL remains the source of truth for URL-layer access; this is a
 * UX-only hide for the HubSwitcher dropdown.
 */
export function deriveUserRole(
  roles: readonly string[],
): "admin" | "member" {
  if (roles.includes("admin")) return "admin";
  return "member";
}

export function HubAppLayout({ children }: HubAppLayoutProps) {
  const location = useLocation();
  const { roles } = useAuth();
  const userRole = deriveUserRole(roles);
  const filteredNav = useMemo(() => getFilteredNavSections(roles), [roles]);
  const { shortcutsOpen, setShortcutsOpen } = useKeyboardShortcuts();

  return (
    <>
      <RouteProgressBar />

      <PortalShell
        currentHubId="aihub"
        portalName="Pulse Agent Hub"
        navigation={filteredNav}
        pathname={location.pathname}
        commandPaletteQuickActions={HUB_COMMAND_PALETTE_QUICK_ACTIONS}
        LinkComponent={RouterLink}
        searchPlaceholder="Search agents, workflows, and incidents..."
        userRole={userRole}
      >
        <ThemeProvider />
        <PinnedNav />
        <div className="px-6 pt-4 hidden md:block">
          <Breadcrumb />
        </div>
        <div className="px-4 pt-4 md:hidden">
          <Breadcrumb />
        </div>
        {children}
        <NotificationToast />
        <OnboardingTour />
      </PortalShell>
      <KeyboardShortcutsModal
        open={shortcutsOpen}
        onClose={() => setShortcutsOpen(false)}
      />
    </>
  );
}
